import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { ReviewSnippetStrip } from "../components/GoogleReviews";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StayIndex() {
  const [audiences, setAudiences] = useState([]);
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.all([axios.get(`${API}/audiences`), axios.get(`${API}/photos`)]).then(([a, p]) => {
      if (!alive) return;
      setAudiences(a.data || []);
      setPhotos(p.data.photos || []);
    });
    return () => { alive = false; };
  }, []);
  const canonical = "https://westbridgehotels.com/stay";
  useSeo({
    title: "Stay With Us — Guides by Traveler Type · Westbridge Inn, Centerville, Iowa",
    description: "Twenty honest guides matching Westbridge Inn's rooms and amenities to your trip — business, truck, crew, family, wedding, medical, and more.",
    canonical,
    jsonLd: [buildBreadcrumbLd([{ label: "Stay With Us", to: "/stay" }])],
  });
  return (
    <main id="main" data-testid="stay-index-page">
      <PageHero
        eyebrow="Stay With Us"
        title="Twenty ways to book — one honest hotel."
        intro="Business travelers, truckers, crews, families, wedding guests, medical travelers. Twenty short guides matching the room and the rate to your trip."
        breadcrumbs={[{ label: "Stay With Us", to: "/stay" }]}
        testId="stay-index-hero"
        compact
      />
      <section className="bg-cream py-16 md:py-24" data-testid="stay-grid">
        <div className="container-app grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {audiences.map((a) => (
            <Link
              key={a.slug}
              to={`/stay/${a.slug}`}
              className="group bg-cream-50 border border-tan p-6 md:p-7 hover:border-brass/60 transition-colors"
              data-testid={`stay-card-${a.slug}`}
            >
              <p className="eyebrow text-brass mb-3">For</p>
              <h2 className="font-display text-xl md:text-2xl text-forest-900 leading-tight mb-3 group-hover:text-brass">
                {a.label}
              </h2>
              <p className="text-sm text-ink/75 leading-relaxed">{a.tagline}</p>
            </Link>
          ))}
        </div>
      </section>
      <CtaBlock />
    </main>
  );
}

export function StayDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    setA(null); setNotFound(false);
    (async () => {
      try {
        const [au, rm, am, pp] = await Promise.all([
          axios.get(`${API}/audiences/${slug}`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/rooms`).then((r) => r.data),
          axios.get(`${API}/amenities`).then((r) => r.data),
          axios.get(`${API}/photos`).then((r) => r.data),
        ]);
        if (!alive) return;
        if (!au) { setNotFound(true); return; }
        setA(au); setRooms(rm || []); setAmenities(am || []); setPhotos(pp.photos || []);
      } catch (e) { setNotFound(true); }
    })();
    return () => { alive = false; };
  }, [slug]);
  const hero = useMemo(() => (a && photos.length ? findPhoto(photos, a.photo_slug) : null), [a, photos]);
  const relRooms = useMemo(() => a ? (a.recommended_rooms || []).map((s) => rooms.find((r) => r.slug === s)).filter(Boolean) : [], [a, rooms]);
  const relAmens = useMemo(() => a ? (a.recommended_amenities || []).map((s) => amenities.find((x) => x.slug === s)).filter(Boolean) : [], [a, amenities]);
  const canonical = a ? `https://westbridgehotels.com/stay/${a.slug}` : undefined;
  useSeo({
    title: a?.meta_title || "Stay with Westbridge",
    description: a?.meta_description || "",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: a ? [
      buildBreadcrumbLd([{ label: "Stay With Us", to: "/stay" }, { label: a.label, to: `/stay/${a.slug}` }]),
      buildFaqLd(a.faqs),
    ] : [],
  });
  if (notFound) return (<main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream"><div className="container-app text-center"><p className="eyebrow text-brass mb-3">404</p><h1 className="font-display text-4xl text-forest-900">Audience guide not found</h1><Link to="/stay" className="btn-ghost-dark mt-6 inline-block">See all audiences</Link></div></main>);
  if (!a) return (<main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream"><div className="container-app"><div className="w-full h-72 bg-tan-200 animate-pulse" /></div></main>);
  return (
    <main id="main" data-testid="stay-detail">
      <PageHero
        eyebrow="Stay With Us"
        title={a.label}
        intro={a.hero_intro}
        photo={hero}
        breadcrumbs={[{ label: "Stay With Us", to: "/stay" }, { label: a.label, to: `/stay/${a.slug}` }]}
        testId="stay-detail-hero"
      />
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <p className="eyebrow mb-4">The pain</p>
            <ul className="space-y-3 mb-10">
              {a.pain_points?.map((p, i) => (<li key={i} className="flex gap-3 text-ink/85 text-[1.02rem] leading-relaxed"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-brass shrink-0" />{p}</li>))}
            </ul>
            <p className="eyebrow mb-4">Why Westbridge works for you</p>
            <p className="text-ink/85 text-lg leading-relaxed">{a.why_westbridge}</p>
            <div className="mt-10 bg-cream-100 border border-brass/40 p-6">
              <p className="eyebrow text-brass mb-2">Book Direct &amp; Save</p>
              <p className="text-forest-900">
                No third-party fees. Best-rate guidance at the front desk. Call for unpublished specials that don't appear on public sites.
              </p>
            </div>
            <div className="mt-8 text-sm text-ink/70 leading-relaxed">{a.rate_note}</div>
          </div>
          <aside className="md:col-span-5">
            <div className="bg-cream-100 border border-tan p-6 md:p-8 mb-6">
              <p className="eyebrow mb-4">Recommended rooms</p>
              <ul className="space-y-2">
                {relRooms.map((r) => (<li key={r.slug}><Link to={`/rooms/${r.slug}`} className="text-forest-900 hover:text-brass" data-testid={`stay-related-room-${r.slug}`}>{r.name} →</Link></li>))}
              </ul>
            </div>
            <div className="bg-cream-100 border border-tan p-6 md:p-8">
              <p className="eyebrow mb-4">Relevant amenities</p>
              <ul className="space-y-2">
                {relAmens.map((am) => (<li key={am.slug}><Link to={`/amenities/${am.slug}`} className="text-forest-900 hover:text-brass" data-testid={`stay-related-amenity-${am.slug}`}>{am.label} →</Link></li>))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
      {a.faqs?.length > 0 && (
        <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="stay-faq">
          <div className="container-app relative grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow mb-3">Common questions</p>
              <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight">For {a.label.toLowerCase()}.</h2>
            </div>
            <div className="md:col-span-8"><FaqAccordion items={a.faqs} testIdPrefix="stay-faq" /></div>
          </div>
        </section>
      )}
      <ReviewSnippetStrip ids={["joe-m", "dorothy-v"]} variant="light" testId="stay-review-strip" />
      <CtaBlock eyebrow="Ready to book?" title="One call. One rate. No surprises." />
    </main>
  );
}
